import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { JuniorLayout } from '../components/layout/JuniorLayout';
import { HOME_THEME as T, ACCENT_HEX } from '../lib/juniorTheme';
import { usePlaySession } from '@/play/contexts/PlaySessionContext';

export default function WelcomePage() {
  const navigate = useNavigate();
  const {
    hasSession,
    isAuthenticated,
    isAuthLoading,
    isTeacher,
    studentProfile,
    login,
    loginWithGoogle,
    signInWithEdusico,
    saveStudentProfile,
    authError,
  } = usePlaySession();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [rut, setRut] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [profileError, setProfileError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  useEffect(() => {
    if (hasSession) {
      navigate('/home', { replace: true, state: { animateEntry: true } });
    }
  }, [hasSession, navigate]);

  if (isAuthLoading) {
    return (
      <JuniorLayout showBar={false}>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-sm font-bold" style={{ color: T.muted }}>
            Conectando con Edusico…
          </p>
        </div>
      </JuniorLayout>
    );
  }

  if (hasSession) {
    return <Navigate to="/home" replace />;
  }

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    try {
      await login(loginEmail, loginPassword);
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'No se pudo iniciar sesión');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogle = async () => {
    setIsLoggingIn(true);
    setLoginError('');
    try {
      await loginWithGoogle();
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Error con Google');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setProfileError('Nombre y apellido son obligatorios');
      return;
    }
    setIsSavingProfile(true);
    setProfileError('');
    const ok = await saveStudentProfile(firstName, lastName, rut || undefined);
    setIsSavingProfile(false);
    if (!ok) setProfileError('No se pudo guardar tu perfil');
    else navigate('/home', { state: { animateEntry: true } });
  };

  const showProfileForm = isAuthenticated && !isTeacher && !studentProfile;

  return (
    <JuniorLayout showBar={false}>
      <div className="min-h-screen flex items-center justify-center px-6 py-12 relative overflow-hidden">
        <div
          className="absolute -top-24 -left-24 h-64 w-64 rounded-full opacity-45 blur-3xl"
          style={{ background: T.accentSoft }}
          aria-hidden
        />
        <div
          className="absolute -bottom-20 -right-20 h-72 w-72 rounded-full opacity-40 blur-3xl"
          style={{ background: T.highlightSoft }}
          aria-hidden
        />
        <div
          className="absolute top-1/3 right-1/4 h-48 w-48 rounded-full opacity-30 blur-3xl"
          style={{ background: ACCENT_HEX.lime + '44' }}
          aria-hidden
        />

        <div
          className="relative w-full max-w-md rounded-[2rem] p-8 sm:p-10 junior-glass"
          style={{ boxShadow: T.glowCoral, border: `2px solid ${T.cardBorder}` }}
        >
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] mb-2" style={{ color: T.muted }}>
            Edusico
          </p>
          <h1
            className="junior-display text-4xl sm:text-5xl mb-2"
            style={{
              background: T.textGradient,
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Junior
          </h1>
          <p className="text-sm mb-6 leading-relaxed font-semibold" style={{ color: T.muted }}>
            {showProfileForm
              ? 'Completa tu perfil para empezar a jugar con la música.'
              : 'Inicia sesión con tu cuenta Edusico y explora ritmo, melodía y más.'}
          </p>

          {showProfileForm ? (
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <label className="block text-sm font-bold" style={{ color: T.text }}>
                Nombre
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-3 rounded-2xl border-2 bg-white/90 font-semibold text-lg"
                style={{ borderColor: T.cardBorder }}
                required
              />
              <label className="block text-sm font-bold" style={{ color: T.text }}>
                Apellido
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-3 rounded-2xl border-2 bg-white/90 font-semibold text-lg"
                style={{ borderColor: T.cardBorder }}
                required
              />
              <label className="block text-sm font-bold" style={{ color: T.text }}>
                RUT (opcional)
              </label>
              <input
                type="text"
                value={rut}
                onChange={(e) => setRut(e.target.value)}
                className="w-full px-4 py-3 rounded-2xl border-2 bg-white/90 font-semibold text-lg"
                style={{ borderColor: T.cardBorder }}
              />
              {profileError && <p className="text-sm font-bold text-red-600">{profileError}</p>}
              <button
                type="submit"
                disabled={isSavingProfile}
                className="w-full py-4 rounded-2xl font-extrabold text-white text-lg disabled:opacity-50"
                style={{ background: ACCENT_HEX.coral, boxShadow: T.glowCoral }}
              >
                {isSavingProfile ? 'Guardando…' : '¡Vamos! →'}
              </button>
            </form>
          ) : (
            <>
              {!isAuthenticated && (
                <div className="space-y-3">
                  <form onSubmit={handleEmailLogin} className="space-y-3">
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="Correo Edusico"
                      className="w-full px-4 py-3 rounded-2xl border-2 bg-white/90 font-semibold text-lg"
                      style={{ borderColor: T.cardBorder }}
                      required
                    />
                    <input
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Contraseña"
                      className="w-full px-4 py-3 rounded-2xl border-2 bg-white/90 font-semibold text-lg"
                      style={{ borderColor: T.cardBorder }}
                      required
                    />
                    <button
                      type="submit"
                      disabled={isLoggingIn}
                      className="w-full py-4 rounded-2xl font-extrabold text-white text-lg disabled:opacity-50"
                      style={{ background: ACCENT_HEX.coral, boxShadow: T.glowCoral }}
                    >
                      {isLoggingIn ? 'Entrando…' : 'Entrar con correo'}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={handleGoogle}
                    disabled={isLoggingIn}
                    className="w-full py-3 rounded-2xl font-bold junior-glass disabled:opacity-50 text-lg"
                    style={{ color: T.text }}
                  >
                    Continuar con Google
                  </button>

                  <button
                    type="button"
                    onClick={signInWithEdusico}
                    className="w-full py-3 rounded-2xl font-bold text-sm"
                    style={{ color: T.muted }}
                  >
                    Ir a edusico.cl para iniciar sesión →
                  </button>
                </div>
              )}

              {isAuthenticated && isTeacher && (
                <button
                  type="button"
                  onClick={() => navigate('/home', { state: { animateEntry: true } })}
                  className="w-full py-4 rounded-2xl font-extrabold text-white text-lg"
                  style={{ background: ACCENT_HEX.mint, boxShadow: T.glowViolet }}
                >
                  Continuar como profesor →
                </button>
              )}

              {(loginError || authError) && (
                <p className="text-sm font-bold text-red-600 mt-3">{loginError || authError}</p>
              )}
            </>
          )}
        </div>
      </div>
    </JuniorLayout>
  );
}
